import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SkeletonLoader from '../../components/SkeletonLoader';
import { useToast } from '../../contexts/ToastContext';
import { fetchBlogDetail, fetchBlogs, fetchBlogCategoriesWithBlogs } from '../../api/blogApi';
import MetaTagsUpdater from '../../components/MetaTagsUpdater'; // Import MetaTagsUpdater
import NewsletterSubscriptionForm from '../../components/NewsletterSubscriptionForm'; // Import the new component

// Helper function to unescape HTML entities
const unescapeHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.documentElement.textContent;
};

function BlogDetail({ setDynamicSeo }) {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToast();

  useEffect(() => {
    const fetchBlogDetails = async () => {
      if (!slug) {
        setLoading(false);
        setBlog(null);
        addToast('error', 'Invalid blog URL. Slug is missing.');
        if (setDynamicSeo) {
          setDynamicSeo({ title: '', description: '', dynamicImage: '' });
        }
        return;
      }

      try {
        setLoading(true);

        const blogResponse = await fetchBlogDetail(slug);
        if (!blogResponse.success) {
          addToast('error', blogResponse.message || 'Failed to fetch blog details.');
          setLoading(false);
          setBlog(null);
          if (setDynamicSeo) {
            setDynamicSeo({ title: '', description: '', dynamicImage: '' });
          }
          return;
        }

        const fetchedBlog = blogResponse.data;

        const categoriesResponse = await fetchBlogCategoriesWithBlogs();
        if (categoriesResponse.success) {
          setCategories(categoriesResponse.data);
        } else {
          setCategories([]);
        }

        const formattedBlog = {
          ...fetchedBlog,
          title: fetchedBlog.title,
          image: fetchedBlog.image,
          content: unescapeHtml(fetchedBlog.description), // Unescape HTML content here
          date: new Date(fetchedBlog.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
          author: fetchedBlog.author || 'Rishabh Gupta',
        };
        setBlog(formattedBlog);

        if (setDynamicSeo) {
          setDynamicSeo({
            title: formattedBlog.title,
            description: fetchedBlog.excerpt,
            dynamicImage: fetchedBlog.image, // Pass the image URL
          });
        }

        const recentBlogsResponse = await fetchBlogs({ limit: 3, paginate: false });
        if (recentBlogsResponse.success) {
          const filteredRecent = recentBlogsResponse.data
            .filter(b => b.slug !== fetchedBlog.slug)
            .slice(0, 3)
            .map(b => ({
              id: b.id,
              title: b.title,
              date: new Date(b.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
              slug: b.slug,
            }));
          setRecentBlogs(filteredRecent);
        } else {
          setRecentBlogs([]);
        }

      } catch (e) {
        console.error("Error fetching blog details:", e);
        addToast('error', 'Failed to load blog details. Please try again later.');
        setBlog(null);
        if (setDynamicSeo) {
          setDynamicSeo({ title: '', description: '', dynamicImage: '' });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBlogDetails();
  }, [slug, addToast, setDynamicSeo]);

  return (
    <div className="min-h-screen bg-bg-dark text-text-gray pt-20">
      <MetaTagsUpdater
        pageKey="BlogDetail" // Use a specific pageKey for blog details
        dynamicTitle={blog?.title || ''}
        dynamicDescription={blog?.excerpt || ''}
        dynamicImage={blog?.image || ''} // Pass dynamicImage
      />
      <div className="container mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          {loading ? (
            <div className="bg-bg-light rounded-lg shadow-lg overflow-hidden p-8 mt-12 animate-pulse">
              <div className="w-full h-64 bg-gray-700 rounded-lg mb-8"></div>
              <div className="h-10 bg-gray-700 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-700 rounded w-1/4 mb-6"></div>
              <div className="h-4 bg-gray-700 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-700 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-700 rounded w-2/3"></div>
            </div>
          ) : !blog ? (
            <div className="pt-20 text-center text-text-white min-h-screen bg-bg-dark">
              <h1 className="text-4xl font-bold mb-4">Blog Post Not Found</h1>
              <p className="text-lg">The blog post you are looking for does not exist or the URL is invalid.</p>
              <Link to="/blogs" className="text-accent-red hover:underline mt-4 inline-block">
                Back to Blogs
              </Link>
            </div>
          ) : (
            <>
              <img src={blog.image} alt={blog.title} className="w-full h-80 object-cover object-top rounded-lg mb-8 shadow-lg" />
              <h1 className="text-5xl font-bold text-text-white text-center mb-10 relative after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-20 after:h-1 after:bg-accent-red after:rounded-sm after:bottom-[-15px]">{blog.title}</h1>
              <p className="text-text-gray text-sm mb-6">
                By <span className="text-accent-red">{blog.author}</span> on {blog.date}
              </p>
              <div
                className="rich-text-content" // Applied custom rich text styling class
                dangerouslySetInnerHTML={{ __html: blog.content }}
              ></div>
            </>
          )}
        </div>

        <div className="lg:col-span-1">
          <div className="bg-gray-800 rounded-lg shadow-lg p-6 mb-8 border border-border-subtle border-b-0">
            <h3 className="text-2xl font-bold text-text-white relative after:content-[''] after:absolute after:bottom-[-4px] after:left-0 after:w-16 after:h-1 after:bg-accent-red after:rounded-sm pb-1">Recent Blogs</h3>
            {loading ? (
              <ul className="space-y-4 mt-4">
                <SkeletonLoader type="blog-card" count={3} />
              </ul>
            ) : recentBlogs.length > 0 ? (
              <ul className="space-y-4 mt-4">
                {recentBlogs.map((recentBlog) => (
                  <li key={recentBlog.id} className="pb-4 border-b border-border-subtle last:border-b-0">
                    <Link to={`/blogs/${recentBlog.slug}`} className="block text-text-gray hover:text-accent-red transition-colors duration-200">
                      <h4 className="text-lg font-semibold">{recentBlog.title}</h4>
                      <p className="text-sm">{recentBlog.date}</p>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-text-gray text-sm">No recent blogs found.</p>
            )}
          </div>

          <div className="bg-gray-800 rounded-lg shadow-lg p-6 mb-8 border border-border-subtle border-b-0">
            <h3 className="text-2xl font-bold text-text-white relative after:content-[''] after:absolute after:bottom-[-4px] after:left-0 after:w-16 after:h-1 after:bg-accent-red after:rounded-sm pb-1">Categories</h3>
            {loading ? (
              <ul className="space-y-3 mt-4">
                {Array.from({ length: 3 }).map((_, index) => (
                  <li key={index} className="h-8 bg-gray-700 rounded mb-2 animate-pulse pb-3 border-b border-border-subtle last:border-b-0"></li>
                ))}
              </ul>
            ) : categories.length > 0 ? (
              <ul className="space-y-3 mt-4">
                {categories.map((category) => (
                  <li key={category.id} className="pb-3 border-b border-border-subtle last:border-b-0">
                    <Link to={`/category/${category.slug}`} className="block text-text-gray hover:text-accent-red transition-colors duration-200"> {/* Updated Link path */}
                      <span className="text-lg">{category.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-text-gray text-sm">No categories found.</p>
            )}
          </div>

          {/* Newsletter Subscription Form */}
          <NewsletterSubscriptionForm />
        </div>
      </div>
    </div>
  );
}

export default BlogDetail;